import styled from "styled-components";
import { Row, Col, Form, Button, Input, FormFeedback, Label, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const RebelColors = {
    // Core colors from style guide
    primary: "#171717",          // Main dark background
    primaryDark: "#0a0a0a",      // Darker backgrounds
    secondary: "#f5f5f5",        // Light mode backgrounds
    accent: "#fdb506",           // Brand gold - CTAs, highlights, borders
    gold: "#fdb506",             // Alias for accent (premium R.E.B.E.L branding)
    success: "#10b981",          // Success states
    error: "#ef4444",            // Error states
    danger: "#ef4444",           // Alias for error
    warning: "#fbbf24",          // Warning states

    // Text colors
    text: "#ffffff",             // Primary text on dark
    textMuted: "#9ca3af",        // Muted/secondary text
    primaryMuted: "#9ca3af",     // Alias for textMuted

    // Gray scale (for UI elements)
    gray50: "#f9fafb",
    gray100: "#f3f4f6",
    gray200: "#e5e7eb",
    gray300: "#d1d5db",
    gray400: "#9ca3af",          // Muted text
    gray500: "#6b7280",
    gray600: "#4b5563",          // Input borders
    gray700: "#374151",          // Card backgrounds, input backgrounds
    gray800: "#1f2937",          // Darker card backgrounds
    gray900: "#111827",

    // Surface colors
    background: "#171717",
    surface: "#374151",          // Input backgrounds, cards
    surfaceLight: "#4b5563",     // Lighter surface
    border: "#4b5563",           // Standard borders
    borderLight: "#6b7280",      // Lighter borders
    borderAccent: "rgba(253, 181, 6, 0.2)",  // Gold accent borders

    // Gradients
    gradientStart: "#000000",
    gradientMid: "#1a1a1a",
    gradientEnd: "#2d2d2d",

    // Shadows
    shadowSm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    shadowMd: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    shadowLg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    shadowXl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
    shadow2xl: "0 25px 50px -12px rgb(0 0 0 / 0.25)"
};

export const RebelContainer = styled.div`
    background: linear-gradient(135deg, #000000 0%, #1a1a1a 25%, #2d2d2d 50%, #1a1a1a 75%, #000000 100%);
    background-attachment: fixed;
    min-height: 100vh;
    color: ${RebelColors.text};
    padding: 0;
    margin: 0;
`;

export const RebelContentWrapper = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;
`;

export const RebelCard = styled.div`
    background: linear-gradient(135deg, #000000 0%, #1f2937 50%, #374151 100%);
    border-radius: 0.75rem;
    padding: 2rem;
    margin: 1.5rem 1rem;
    border: 1px solid ${RebelColors.gold};
    box-shadow: ${RebelColors.shadow2xl};
    color: ${RebelColors.text};

    p {
        color: ${RebelColors.text};
    }

    label {
        color: ${RebelColors.text};
    }

    a {
        color: ${RebelColors.gold};
        transition: all 0.2s ease-in-out;

        &:hover {
            color: #f59e0b;
        }
    }
`;

export const RebelHeaderCard = styled(RebelCard)`
    background: linear-gradient(135deg, #000000 0%, #1f2937 50%, #374151 100%);
    border: 2px solid ${RebelColors.gold};
    text-align: center;
    margin-bottom: 2rem;
`;

export const RebelClientCard = styled(RebelCard)`
    background: linear-gradient(135deg, #000000 0%, #1f2937 50%, #374151 100%);
    border: 1px solid ${RebelColors.gold};
    backdrop-filter: blur(10px);
    position: relative;
    overflow: hidden;
    max-width: 1200px;
    margin-left: auto;
    margin-right: auto;

    &::before {
        content: '';
        position: absolute;
        top: 150px;
        left: 50%;
        transform: translateX(-50%);
        width: 600px;
        height: 750px;
        background-image: url('/Moniker Logo-04.svg');
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        opacity: 0.03;
        pointer-events: none;
        z-index: 0;
    }

    > * {
        position: relative;
        z-index: 1;
    }

    p {
        color: ${RebelColors.text};
    }

    label {
        color: ${RebelColors.text};
    }

    a {
        color: ${RebelColors.gold};
    }
`;

// Highlight box for results/summaries (style guide pattern)
export const RebelHighlightBox = styled.div`
    background: linear-gradient(135deg, rgba(253, 181, 6, 0.1) 0%, rgba(253, 181, 6, 0.05) 50%, transparent 100%);
    border: 1px solid rgba(253, 181, 6, 0.2);
    border-radius: 0.75rem;
    padding: 1rem;
    color: ${RebelColors.text};
`;

export const RebelTitle = styled.h1`
    color: ${RebelColors.gold};
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
    letter-spacing: 2px;
`;

export const RebelSubtitle = styled.p`
    color: ${RebelColors.textMuted};
    font-size: 1.125rem;
    font-style: italic;
    margin-bottom: 0;
    letter-spacing: 1px;
`;

export const RebelSectionTitle = styled.h2`
    color: ${RebelColors.text};
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1rem;
    border-bottom: 2px solid rgba(253, 181, 6, 0.3);
    padding-bottom: 0.5rem;
`;

export const RebelForm = styled(Form)`
    background: linear-gradient(135deg, #000000 0%, #1f2937 50%, #374151 100%);
    border-radius: 0.75rem;
    padding: 1.5rem;
    border: 1px solid ${RebelColors.border};
`;

export const RebelFormGroup = styled.div`
    margin-bottom: 1.25rem;
`;

export const RebelLabel = styled(Label)`
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    color: ${RebelColors.gold};
    margin-bottom: 0.5rem;
`;

export const RebelInput = styled(Input)`
    background-color: ${RebelColors.gray700};
    border: 1px solid ${RebelColors.gray600};
    color: ${RebelColors.text};
    border-radius: 0.5rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    transition: all 0.2s ease-in-out;

    &:focus {
        outline: none;
        background-color: ${RebelColors.gray700};
        border-color: ${RebelColors.gold};
        box-shadow: 0 0 0 2px rgba(253, 181, 6, 0.2);
        color: ${RebelColors.text};
    }

    &::placeholder {
        color: ${RebelColors.gray400};
    }

    &:hover {
        border-color: ${RebelColors.borderLight};
    }
`;

export const RebelTextarea = styled(RebelInput).attrs({ as: 'textarea' })`
    min-height: 100px;
    resize: vertical;
`;

export const RebelSelect = styled(RebelInput).attrs({ as: 'select' })`
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
    background-position: right 0.5rem center;
    background-repeat: no-repeat;
    background-size: 1.5em 1.5em;
    padding-right: 2.5rem;

    &:focus {
        outline: none;
        border-color: ${RebelColors.gold};
        box-shadow: 0 0 0 2px rgba(253, 181, 6, 0.2);
    }

    option {
        background-color: ${RebelColors.gray700};
        color: ${RebelColors.text};
        padding: 0.5rem 0.75rem;
        border: none;
    }
`;

// Primary Button (dark gradient)
export const RebelButton = styled(Button)`
    background: linear-gradient(90deg, #171717 0%, #262626 100%);
    border: none;
    border-radius: 0.5rem;
    padding: 0.75rem 1.5rem;
    font-weight: 500;
    color: ${RebelColors.text};
    transition: all 0.2s ease-in-out;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    svg {
        color: ${RebelColors.text};
    }

    &:hover {
        transform: scale(1.02);
        box-shadow: ${RebelColors.shadowMd};
        color: ${RebelColors.text};
    }

    &:active {
        transform: scale(1);
    }

    &:focus {
        box-shadow: 0 0 0 2px rgba(253, 181, 6, 0.3);
        color: ${RebelColors.text};
    }
`;

// Accent Button (Gold CTA)
export const RebelAccentButton = styled(Button)`
    background: linear-gradient(90deg, #fdb506 0%, #f59e0b 100%);
    border: 2px solid #fdb506;
    border-radius: 0.5rem;
    padding: 0.75rem 1.5rem;
    font-weight: 600;
    color: #000000;
    transition: all 0.3s ease-in-out;
    box-shadow: ${RebelColors.shadowLg};
    display: inline-flex;
    align-items: center;
    justify-content: center;

    svg {
        color: #000000;
    }

    &:hover {
        transform: scale(1.02);
        box-shadow: ${RebelColors.shadowXl};
        color: #000000;
        border-color: #f59e0b;
    }

    &:active {
        transform: scale(1);
    }

    &:focus {
        box-shadow: 0 0 0 3px rgba(253, 181, 6, 0.3);
        color: #000000;
    }
`;

// Ghost/Outline Button
export const RebelSecondaryButton = styled(Button)`
    background: transparent;
    border: 2px solid ${RebelColors.gold};
    border-radius: 0.5rem;
    padding: 0.75rem 1.5rem;
    font-weight: 500;
    color: ${RebelColors.gold};
    transition: all 0.3s ease-in-out;

    svg {
        color: ${RebelColors.gold};
    }

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: ${RebelColors.gold};
        border-color: ${RebelColors.gold};
    }

    &:focus {
        box-shadow: 0 0 0 2px rgba(253, 181, 6, 0.2);
        color: ${RebelColors.gold};
    }
`;

export const RebelDangerButton = styled(Button)`
    background: linear-gradient(90deg, ${RebelColors.danger} 0%, #dc2626 100%);
    border: none;
    border-radius: 0.5rem;
    padding: 0.75rem 1.5rem;
    font-weight: 500;
    color: ${RebelColors.text};
    transition: all 0.2s ease-in-out;

    &:hover {
        transform: scale(1.02);
        box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
        color: ${RebelColors.text};
    }
`;

export const RebelSuccessButton = styled(Button)`
    background: linear-gradient(90deg, ${RebelColors.success} 0%, #059669 100%);
    border: none;
    border-radius: 0.5rem;
    padding: 0.75rem 1.5rem;
    font-weight: 500;
    color: ${RebelColors.text};
    transition: all 0.2s ease-in-out;

    &:hover {
        transform: scale(1.02);
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        color: ${RebelColors.text};
    }
`;

export const RebelIcon = styled(FontAwesomeIcon)`
    color: ${RebelColors.gold};
    margin-right: 0.5rem;
    font-size: 1.2rem;
`;

export const RebelStatusBadge = styled.span`
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    ${props => {
        switch (props.status) {
            case 'active':
                return `
                    background: ${RebelColors.success};
                    color: white;
                `;
            case 'pending':
                return `
                    background: ${RebelColors.warning};
                    color: #000000;
                `;
            case 'inactive':
                return `
                    background: ${RebelColors.danger};
                    color: white;
                `;
            default:
                return `
                    background: ${RebelColors.textMuted};
                    color: ${RebelColors.background};
                `;
        }
    }}
`;

export const RebelPriceDisplay = styled.div`
    background: linear-gradient(135deg, rgba(253, 181, 6, 0.1) 0%, rgba(253, 181, 6, 0.05) 50%, transparent 100%);
    border: 1px solid rgba(253, 181, 6, 0.2);
    border-radius: 0.75rem;
    padding: 1rem;
    text-align: center;
    margin: 1rem 0;
`;

export const RebelPrice = styled.span`
    color: ${RebelColors.gold};
    font-size: 1.8rem;
    font-weight: 700;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
`;

export const RebelPriceLabel = styled.p`
    color: ${RebelColors.textMuted};
    font-size: 0.875rem;
    margin: 0.25rem 0 0 0;
    text-transform: uppercase;
    letter-spacing: 1px;
`;

export const RebelGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    max-width: 100%;
    gap: 1.5rem;
    margin: 1.5rem 0;

    @media (min-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: 1280px) {
        grid-template-columns: repeat(3, 1fr);
    }
`;

export const RebelListItem = styled.div`
    background: linear-gradient(135deg, #000000 0%, #1f2937 50%, #374151 100%);
    border: 1px solid ${RebelColors.border};
    border-radius: 0.5rem;
    padding: 1rem;
    margin-bottom: 0.75rem;
    transition: all 0.2s ease-in-out;

    &:hover {
        border-color: rgba(253, 181, 6, 0.3);
    }
`;

export const RebelFormFeedback = styled(FormFeedback)`
    color: ${RebelColors.danger};
    font-size: 0.85rem;
    margin-top: 0.25rem;
`;

export const RebelLoadingSpinner = styled.div`
    display: inline-block;
    width: 3rem;
    height: 3rem;
    border: 4px solid currentColor;
    border-right-color: transparent;
    border-radius: 9999px;
    animation: spin 1s linear infinite;
    color: ${RebelColors.gold};

    @keyframes spin {
        to { transform: rotate(360deg); }
    }
`;

export const RebelEmptyState = styled.div`
    text-align: center;
    padding: 3rem 1.5rem;
    color: ${RebelColors.textMuted};

    h3 {
        color: ${RebelColors.text};
        margin-bottom: 1rem;
    }

    p {
        font-size: 1.1rem;
        margin-bottom: 1.5rem;
    }
`;

export const RebelSpacer = styled.div`
    height: ${props => props.height || '1.5rem'};
`;

export const RebelRow = styled(Row)`
    margin: 0;
`;

export const RebelCol = styled(Col)`
    padding: 0.75rem;
`;

// Divider component
export const RebelDivider = styled.hr`
    border: none;
    border-bottom: 1px solid rgba(253, 181, 6, 0.2);
    margin: 1.5rem 0;
`;

export const RebelModal = styled(Modal)`
    .modal-content {
        background: linear-gradient(135deg, #000000 0%, #1f2937 50%, #374151 100%);
        border: 2px solid ${RebelColors.gold};
        border-radius: 0.75rem;
        color: ${RebelColors.text};
        box-shadow: ${RebelColors.shadow2xl};
        animation: fadeIn 0.3s ease-out forwards;
    }

    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }
`;

export const RebelModalHeader = styled(ModalHeader)`
    background: transparent;
    border-bottom: 1px solid rgba(253, 181, 6, 0.2);
    color: ${RebelColors.text};
    border-radius: 0.75rem 0.75rem 0 0;
    padding: 1.5rem;

    .modal-title {
        color: ${RebelColors.gold};
        font-weight: 600;
    }

    /* Bootstrap 4 uses .close class, not .btn-close */
    .close {
        color: ${RebelColors.danger} !important;
        text-shadow: none !important;
        opacity: 0.7 !important;
        transition: all 0.2s ease-in-out;
        font-weight: 700;
        font-size: 1.5rem;
        line-height: 1;

        span {
            display: none;
        }

        &::after {
            content: '×';
        }

        &:hover {
            color: #dc2626 !important;
            opacity: 1 !important;
        }

        &:focus {
            outline: none;
        }
    }

    /* Support for both Bootstrap 4 (.close) and Bootstrap 5 (.btn-close) */
    .btn-close {
        background: transparent !important;
        opacity: 0.7 !important;
        transition: all 0.2s ease-in-out;
        color: ${RebelColors.danger} !important;
        text-shadow: none !important;

        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23ef4444'%3e%3cpath d='M.293.293a1 1 0 011.414 0L8 6.586 14.293.293a1 1 0 111.414 1.414L9.414 8l6.293 6.293a1 1 0 01-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 01-1.414-1.414L6.586 8 .293 1.707a1 1 0 010-1.414z'/%3e%3c/svg%3e") !important;

        &:hover {
            opacity: 1 !important;
            color: #dc2626 !important;
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23dc2626'%3e%3cpath d='M.293.293a1 1 0 011.414 0L8 6.586 14.293.293a1 1 0 111.414 1.414L9.414 8l6.293 6.293a1 1 0 01-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 01-1.414-1.414L6.586 8 .293 1.707a1 1 0 010-1.414z'/%3e%3c/svg%3e") !important;
        }
    }
`;

export const RebelModalBody = styled(ModalBody)`
    background: transparent;
    color: ${RebelColors.text};
    padding: 1.5rem;
`;

export const RebelModalFooter = styled(ModalFooter)`
    background: transparent;
    border-top: 1px solid rgba(253, 181, 6, 0.2);
    border-radius: 0 0 0.75rem 0.75rem;
    padding: 1rem 1.5rem;
`;

// Modal icon container (for confirmation dialogs)
export const RebelModalIcon = styled.div`
    width: 4rem;
    height: 4rem;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
    background: linear-gradient(135deg, rgba(253, 181, 6, 0.2) 0%, rgba(253, 181, 6, 0.1) 100%);
    border: 2px solid rgba(253, 181, 6, 0.3);

    svg {
        width: 2rem;
        height: 2rem;
        color: ${RebelColors.gold};
    }
`;